import { useMemo } from "react"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import { usePortfolioDailyQuery } from "@/v2-backoffice-client/portfolio/portfolio.daily.query"
import type { PortfolioWindow } from "@/v2-backoffice-client/portfolio/portfolio.filters"

export type UsePortfolioDailyDataInput = {
  /** The Overview chart's selected window — widens how far back we fetch. */
  timeWindow: PortfolioWindow
  /**
   * Resolved server-side scope (facets resolved to ids by the caller);
   * null = the whole portfolio.
   */
  customerIds: string[] | null
  excludeCustomerIds: string[]
  excludeUserIds: number[]
}

/**
 * The Portfolio's ONE daily-rows fetch. One payload serves two masters: the
 * Overview chart/KPI (selected window + a SAME-LENGTH baseline before it for
 * the comparison) and the Health tab's product/area trends (always trailing
 * 28d-vs-28d anchored at NOW). So it fetches from the earliest of those
 * needs up to now — without the baselines in the payload every trend divides
 * by nothing and reads +2000%.
 */
export function usePortfolioDailyData(input: UsePortfolioDailyDataInput) {
  const { timeWindow, customerIds, excludeCustomerIds, excludeUserIds } = input

  // Derived from the memoized window so the query variables stay
  // referentially stable.
  const fetchFrom = useMemo(() => {
    const reachDays = Math.max(2 * timeWindow.days, 56)
    const windowFloor = new Date(
      timeWindow.end.getTime() - reachDays * ONE_DAY_IN_MS,
    )
    // Date.now() only re-evaluates when the timeframe changes — day-level
    // drift within a session doesn't matter, referential stability does.
    const nowFloor = new Date(Date.now() - 56 * ONE_DAY_IN_MS)
    return [timeWindow.from, windowFloor, nowFloor].reduce((min, date) =>
      date < min ? date : min,
    )
  }, [timeWindow])

  const { data, error } = usePortfolioDailyQuery({
    variables: {
      from: fetchFrom.toISOString(),
      // Always up to now (not the window end): the Health-tab trends are
      // anchored at today even when the Overview shows a past quarter.
      to: null,
      customerIds,
      excludeCustomerIds:
        excludeCustomerIds.length > 0 ? excludeCustomerIds : null,
      excludeUserIds: excludeUserIds.length > 0 ? excludeUserIds : null,
    },
  })

  const areaDays = useMemo(
    () => data?.getUsageAreasDailyForPortfolio ?? [],
    [data?.getUsageAreasDailyForPortfolio],
  )

  return { areaDays, error }
}
